/* eslint-disable react-refresh/only-export-components -- intentional: shared module co-exports helpers with components */
import React, { createContext, useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => undefined });

export const useToast = () => useContext(ToastContext);

let toastCounter = 0;

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0);    opacity: 1; }
  to   { transform: translateX(100%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
`;

const toastColors: Record<ToastType, string> = {
  success: 'rgba(52, 211, 153, 0.15)',
  error: 'rgba(248, 113, 113, 0.15)',
  info: 'rgba(124, 140, 255, 0.15)',
};

const toastBorders: Record<ToastType, string> = {
  success: 'rgba(52, 211, 153, 0.4)',
  error: 'rgba(248, 113, 113, 0.4)',
  info: 'rgba(124, 140, 255, 0.4)',
};

const toastIcons: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
};

const ToastChip = styled.div<{ $type: ToastType; $leaving: boolean }>`
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 12px;
  border: 1px solid ${({ $type }) => toastBorders[$type]};
  background: ${({ $type }) => toastColors[$type]};
  backdrop-filter: blur(16px);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  min-width: 260px;
  max-width: 380px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  animation: ${({ $leaving }) => ($leaving ? slideOut : slideIn)} 0.25s ease forwards;
  cursor: pointer;
`;

const Icon = styled.span<{ $type: ToastType }>`
  font-size: 14px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${({ $type }) => toastBorders[$type]};
  flex-shrink: 0;
`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<(ToastItem & { leaving?: boolean })[]>([]);

  const removeToast = useCallback((id: number) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 250);
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = ++toastCounter;
      setItems((prev) => [...prev, { id, message, type }]);
      setTimeout(() => removeToast(id), 3500);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer>
        {items.map((item) => (
          <ToastChip key={item.id} $type={item.type} $leaving={!!item.leaving} onClick={() => removeToast(item.id)}>
            <Icon $type={item.type}>{toastIcons[item.type]}</Icon>
            {item.message}
          </ToastChip>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};
